import { useState } from 'react'
import {
  ArrowUpRight,
  Cloud,
  Code2,
  Layers,
  Menu,
  Network,
  Receipt,
  Server,
  ShoppingBag,
  UtensilsCrossed,
  X,
} from 'lucide-react'

const saasProducts = [
  {
    name: 'Pymees',
    url: 'https://pymees.store/',
    tag: 'E-Commerce / SaaS',
    description:
      'Plataforma de comercio electrónico y tiendas online optimizada para PYMES.',
    logo: '/images/pymees-iso.png?v=2',
    icon: ShoppingBag,
    accent: 'from-blue-600 to-emerald-500',
  },
  {
    name: 'Pídelo Chévere',
    url: 'http://pidelo-chevere.food/',
    tag: 'Gastronomía / POS',
    description:
      'Sistema integral de gestión de comensales, pedidos y menús digitales para restaurantes.',
    logo: '/images/pidelo-chevere-iso.png?v=2',
    icon: UtensilsCrossed,
    accent: 'from-teal-500 to-orange-400',
  },
  {
    name: 'Nexus Invoices',
    url: 'https://invoices.nexusdevtech.com/',
    tag: 'Fintech / Facturación',
    description:
      'Software de facturación, control financiero y generación de comprobantes digitales.',
    logo: '/images/nexus-invoices-iso.png?v=1',
    icon: Receipt,
    accent: 'from-blue-600 to-cyan-400',
  },
  {
    name: 'Protegi2 Cloud',
    url: 'https://www.protegi2.cloud/',
    tag: 'Cloud / Seguridad',
    description:
      'Plataforma de seguridad, respaldo en la nube y gestión remota de activos.',
    logo: '/images/protegi2-iso.png?v=2',
    icon: Cloud,
    accent: 'from-blue-500 to-emerald-400',
  },
]

const services = [
  {
    title: 'Desarrollo Web & Móvil',
    description:
      'Aplicaciones modernas, performantes y responsivas con stacks de producción.',
    icon: Code2,
  },
  {
    title: 'Arquitectura Multi-Tenant & SaaS',
    description:
      'Diseño de plataformas escalables, aislamiento de datos y billing listo para crecer.',
    icon: Layers,
  },
  {
    title: 'Integraciones & APIs',
    description:
      'Pasarelas de pago, facturación electrónica y conectores entre sistemas críticos.',
    icon: Network,
  },
  {
    title: 'DevOps e Infraestructura Cloud',
    description:
      'CI/CD, contenedores, monitoreo y despliegues seguros en la nube.',
    icon: Server,
  },
]

const requirementOptions = [
  'Desarrollo a medida',
  'Consulta SaaS',
  'Infraestructura',
]

function scrollToId(id) {
  const el = document.getElementById(id)
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

export default function LandingNexus() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [form, setForm] = useState({
    name: '',
    email: '',
    requirement: requirementOptions[0],
    message: '',
  })
  const [submitted, setSubmitted] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setSubmitted(true)
    setForm({
      name: '',
      email: '',
      requirement: requirementOptions[0],
      message: '',
    })
  }

  const navLinks = [
    { label: 'Ecosistema SaaS', id: 'ecosistema' },
    { label: 'Servicios a Medida', id: 'servicios' },
    { label: 'Contacto', id: 'contacto' },
  ]

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100 selection:bg-emerald-500/30">
      {/* Ambient background */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 overflow-hidden"
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(0,114,255,0.18),_transparent_55%),radial-gradient(ellipse_at_bottom_right,_rgba(16,185,129,0.14),_transparent_50%)]" />
        <div
          className="absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(148,163,184,0.35) 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />
      </div>

      {/* Navbar */}
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-[#020617]/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:h-20 lg:px-8">
          <a
            href="#inicio"
            onClick={(e) => {
              e.preventDefault()
              scrollToId('inicio')
              setMenuOpen(false)
            }}
            className="flex items-center gap-2.5"
          >
            <img
              src="/images/isologo-nexus.png?v=2"
              alt="NexusDevTech"
              className="h-9 w-9 rounded-full object-cover sm:h-10 sm:w-10"
            />
            <span className="text-sm font-semibold tracking-tight sm:text-base">
              <span className="text-white">nexus</span>
              <span className="bg-gradient-to-r from-blue-500 to-emerald-400 bg-clip-text text-transparent">
                dev
              </span>
              <span className="text-white">tech</span>
              <span className="text-xs font-normal text-slate-400">.com</span>
            </span>
          </a>

          <nav className="hidden items-center gap-8 md:flex">
            {navLinks.map((link) => (
              <button
                key={link.id}
                type="button"
                onClick={() => scrollToId(link.id)}
                className="text-sm text-slate-300 transition hover:text-white"
              >
                {link.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => scrollToId('contacto')}
              className="rounded-lg bg-gradient-to-r from-blue-600 to-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-blue-600/20 transition hover:brightness-110"
            >
              Cotizar Proyecto
            </button>
          </nav>

          <button
            type="button"
            className="inline-flex rounded-lg border border-white/10 p-2 text-slate-200 md:hidden"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {menuOpen && (
          <div className="border-t border-white/5 bg-[#020617]/95 px-4 py-4 md:hidden">
            <div className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  type="button"
                  onClick={() => {
                    scrollToId(link.id)
                    setMenuOpen(false)
                  }}
                  className="rounded-lg px-3 py-2 text-left text-sm text-slate-200 hover:bg-white/5"
                >
                  {link.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  scrollToId('contacto')
                  setMenuOpen(false)
                }}
                className="mt-1 rounded-lg bg-gradient-to-r from-blue-600 to-emerald-500 px-4 py-2.5 text-sm font-semibold text-white"
              >
                Cotizar Proyecto
              </button>
            </div>
          </div>
        )}
      </header>

      <main>
        {/* Hero */}
        <section
          id="inicio"
          className="relative overflow-hidden px-4 pb-20 pt-28 sm:px-6 sm:pt-32 lg:px-8 lg:pb-28 lg:pt-40"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute left-1/2 top-24 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-gradient-to-br from-blue-600/25 to-emerald-500/20 blur-3xl"
          />

          <div className="relative mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.28em] text-emerald-400/90">
                Connect · Build · Innovate
              </p>
              <h1 className="text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl xl:text-[3.25rem]">
                Transformamos Arquitecturas Complejas en{' '}
                <span className="bg-gradient-to-r from-blue-500 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                  Software Escalable
                </span>
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
                Plataformas SaaS listas para usar e ingeniería de software a
                medida para impulsar operaciones digitales sólidas.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={() => scrollToId('ecosistema')}
                  className="inline-flex items-center justify-center rounded-lg bg-gradient-to-r from-blue-600 to-emerald-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition hover:brightness-110"
                >
                  Explorar Productos SaaS
                </button>
                <button
                  type="button"
                  onClick={() => scrollToId('contacto')}
                  className="inline-flex items-center justify-center rounded-lg border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white transition hover:border-emerald-400/40 hover:bg-white/10"
                >
                  Contratar Desarrollo
                </button>
              </div>
            </div>

            <div className="relative mx-auto w-full max-w-md lg:max-w-none">
              <div className="absolute -inset-4 rounded-full bg-gradient-to-r from-blue-600/20 to-emerald-500/20 blur-2xl" />
              <img
                src="/images/portada-nexus.png"
                alt="NexusDevTech — Connect, Build, Innovate"
                className="relative mx-auto w-full max-w-sm drop-shadow-2xl lg:max-w-md"
              />
            </div>
          </div>
        </section>

        {/* Ecosistema SaaS */}
        <section id="ecosistema" className="scroll-mt-24 px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="mx-auto max-w-6xl">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-widest text-blue-400">
                Productos
              </p>
              <h2 className="mt-2 text-2xl font-bold text-white sm:text-3xl">
                Ecosistema de Soluciones SaaS
              </h2>
              <p className="mt-3 text-slate-400">
                Plataformas listas para operar, integradas bajo la ingeniería de
                NexusDevTech.
              </p>
            </div>

            <div className="mt-10 grid gap-5 sm:grid-cols-2">
              {saasProducts.map((product) => {
                const Icon = product.icon
                return (
                  <article
                    key={product.name}
                    className="group relative overflow-hidden rounded-2xl border border-white/10 bg-slate-900/60 p-6 transition duration-300 hover:-translate-y-1 hover:border-emerald-400/30 hover:shadow-[0_0_40px_rgba(16,185,129,0.12)]"
                  >
                    <div
                      className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r ${product.accent} opacity-60`}
                    />
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3">
                        {product.logo ? (
                          <img
                            src={product.logo}
                            alt=""
                            className="h-12 w-12 rounded-xl object-contain"
                          />
                        ) : (
                          <div
                            className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${product.accent} text-white`}
                          >
                            <Icon size={22} />
                          </div>
                        )}
                        <div>
                          <h3 className="text-lg font-semibold text-white">
                            {product.name}
                          </h3>
                          <span className="mt-1 inline-flex rounded-full border border-white/10 bg-white/5 px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-300">
                            {product.tag}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="mt-4 text-sm leading-relaxed text-slate-400">
                      {product.description}
                    </p>
                    <a
                      href={product.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-400 transition group-hover:text-emerald-300"
                    >
                      Probar la app
                      <ArrowUpRight
                        size={16}
                        className="transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      />
                    </a>
                  </article>
                )
              })}
            </div>
          </div>
        </section>

        {/* Servicios */}
        <section id="servicios" className="scroll-mt-24 px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="mx-auto max-w-6xl">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-widest text-emerald-400">
                Ingeniería
              </p>
              <h2 className="mt-2 text-2xl font-bold text-white sm:text-3xl">
                Servicios de Desarrollo a Medida
              </h2>
              <p className="mt-3 text-slate-400">
                Capacidad senior para diseñar, construir y operar software que
                escala con tu negocio.
              </p>
            </div>

            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {services.map((service) => {
                const Icon = service.icon
                return (
                  <article
                    key={service.title}
                    className="rounded-2xl border border-white/10 bg-gradient-to-b from-slate-900/80 to-slate-950/80 p-5 transition hover:border-blue-500/30"
                  >
                    <div className="mb-4 inline-flex rounded-xl bg-gradient-to-br from-blue-600/20 to-emerald-500/20 p-3 text-emerald-400 ring-1 ring-white/10">
                      <Icon size={22} />
                    </div>
                    <h3 className="text-base font-semibold text-white">
                      {service.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-400">
                      {service.description}
                    </p>
                  </article>
                )
              })}
            </div>
          </div>
        </section>

        {/* Contacto */}
        <section id="contacto" className="scroll-mt-24 px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="mx-auto max-w-3xl">
            <div className="overflow-hidden rounded-3xl border border-white/10 bg-slate-900/70 shadow-2xl shadow-blue-950/40">
              <div className="h-1 w-full bg-gradient-to-r from-blue-600 to-emerald-500" />
              <div className="p-6 sm:p-8 lg:p-10">
                <h2 className="text-2xl font-bold text-white sm:text-3xl">
                  Hablemos de tu proyecto
                </h2>
                <p className="mt-2 text-slate-400">
                  Cuéntanos qué necesitas y te respondemos con una propuesta
                  clara.
                </p>

                <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <label className="block">
                      <span className="mb-1.5 block text-sm font-medium text-slate-300">
                        Nombre
                      </span>
                      <input
                        required
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Tu nombre"
                        className="w-full rounded-xl border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1.5 block text-sm font-medium text-slate-300">
                        Email
                      </span>
                      <input
                        required
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="tu@empresa.com"
                        className="w-full rounded-xl border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20"
                      />
                    </label>
                  </div>

                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium text-slate-300">
                      Tipo de Requerimiento
                    </span>
                    <select
                      name="requirement"
                      value={form.requirement}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white outline-none transition focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20"
                    >
                      {requirementOptions.map((opt) => (
                        <option key={opt} value={opt} className="bg-slate-900">
                          {opt}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium text-slate-300">
                      Mensaje
                    </span>
                    <textarea
                      required
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows={5}
                      placeholder="Describe tu necesidad, plazos y objetivos..."
                      className="w-full resize-y rounded-xl border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20"
                    />
                  </label>

                  <button
                    type="submit"
                    className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-emerald-500 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition hover:brightness-110 sm:w-auto"
                  >
                    Enviar mensaje
                  </button>

                  {submitted && (
                    <p className="text-sm text-emerald-400" role="status">
                      Gracias. Recibimos tu mensaje. Te contactaremos pronto.
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="flex items-center gap-2">
            <img
              src="/images/isologo-nexus.png?v=2"
              alt=""
              className="h-7 w-7 rounded-full object-cover"
            />
            <span className="text-sm text-slate-400">
              <span className="text-white">nexus</span>
              <span className="text-emerald-400">dev</span>
              <span className="text-white">tech</span>
              <span className="text-slate-500">.com</span>
            </span>
          </div>
          <p className="text-center text-sm text-slate-500">
            © {new Date().getFullYear()} NexusDevTech. Todos los derechos
            reservados.
          </p>
        </div>
      </footer>
    </div>
  )
}
